import {
  DEFAULT_HOME_PREVIEW_GRID_THICKNESS,
  DEFAULT_HOME_PREVIEW_THEME,
  HOME_PREVIEW_FALLBACK_COLOR,
  type HomePreviewTheme,
} from '@/components/home/constants'
import { hexStringToNumber, normalizeHexNumber } from '@/utils/color'
import type { VoxelData } from '@/voxelTypes'

export type { HomePreviewTheme, HomePreviewThemePreset } from '@/components/home/constants'

export function homePreviewDocumentPalette(theme: HomePreviewTheme): VoxelData[] {
  const source = Array.isArray(theme.paletteItems) && theme.paletteItems.length > 0
    ? theme.paletteItems
    : DEFAULT_HOME_PREVIEW_THEME.paletteItems
  const colors = homePreviewPaletteColors(theme)

  return source.map((item, index) => {
    const next = normalizePaletteItem(item)
    if (index === 0) next.color = colors[0] ?? HOME_PREVIEW_FALLBACK_COLOR
    return next
  })
}

export function homePreviewPaletteColors(theme: HomePreviewTheme): number[] {
  const source = Array.isArray(theme.paletteColors) && theme.paletteColors.length > 0
    ? theme.paletteColors
    : DEFAULT_HOME_PREVIEW_THEME.paletteColors
  const colors = source
    .filter((color) => typeof color === 'number' && Number.isFinite(color))
    .map(normalizeHexNumber)
  return colors.length > 0 ? colors : [HOME_PREVIEW_FALLBACK_COLOR]
}

function normalizePaletteItem(item: VoxelData): VoxelData {
  const next: VoxelData = {
    ...item,
    type: typeof item.type === 'string' && item.type.length > 0 ? item.type : 'solid',
  }
  if (typeof item.color === 'number' && Number.isFinite(item.color)) {
    next.color = normalizeHexNumber(item.color)
  } else {
    delete next.color
  }
  return next
}

export function resolvedHomePreviewGridThickness(theme: HomePreviewTheme): number {
  return typeof theme.gridThickness === 'number' && Number.isFinite(theme.gridThickness)
    ? Math.max(0.002, Math.min(0.2, theme.gridThickness))
    : DEFAULT_HOME_PREVIEW_THEME.gridThickness ?? DEFAULT_HOME_PREVIEW_GRID_THICKNESS
}

export function homePreviewThemeToDocumentMeta(theme: HomePreviewTheme) {
  const paletteColors = homePreviewPaletteColors(theme)
  return {
    backgroundColor: hexStringToNumber(theme.backgroundColor),
    palette: homePreviewDocumentPalette(theme),
    theme: {
      paletteColors,
    },
    boundsAppearance: {
      colors: {
        ground: hexStringToNumber(theme.baseColor),
        grid: hexStringToNumber(theme.guideColor),
      },
      grid: {
        thickness: resolvedHomePreviewGridThickness(theme),
      },
    },
  }
}
